import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  showCharacterCount?: boolean;
  maxLength?: number;
  variant?: 'default' | 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
  required?: boolean;
  labelClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      showCharacterCount = false,
      maxLength,
      variant = 'default',
      size = 'md',
      disabled,
      id,
      labelClassName,
      required = false,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const currentLength = props.value?.toString().length || 0;

    const baseClasses = cn(
      'block w-full border rounded-md shadow-sm transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'dark:bg-gray-700 dark:border-gray-600 dark:text-white',
      'dark:focus:ring-blue-400 dark:focus:border-blue-400',
      {
        'w-full': fullWidth,
        'border-red-300 focus:ring-red-500 focus:border-red-500 dark:border-red-500': error,
        'border-gray-300 dark:border-gray-600': !error && variant === 'default',
        'bg-gray-50 border-gray-300 dark:bg-gray-800 dark:border-gray-600': variant === 'filled',
        'border-2 border-gray-300 dark:border-gray-600': variant === 'outlined',
        'px-3 py-2 text-sm': size === 'sm',
        'px-4 py-2.5 text-base': size === 'md',
        'px-4 py-3 text-lg': size === 'lg',
        'pl-10': leftIcon,
        'pr-10': rightIcon,
      },
      className
    );

    return (
      <div className={cn('space-y-2', { 'w-full': fullWidth })}>
        {label && (
          <label 
            className={
              cn(
                'block text-sm font-medium text-gray-700 dark:text-gray-300',
                labelClassName
              )
            }
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
              {leftIcon}
            </div>
          )}
          
          <textarea
            ref={ref}
            id={textareaId}
            className={baseClasses}
            disabled={disabled}
            maxLength={maxLength}
            {...props}
          />
          
          {rightIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>

        {(error || helperText || (showCharacterCount && maxLength)) && (
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
              {helperText && !error && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {helperText}
                </p>
              )}
            </div>
            
            {showCharacterCount && maxLength && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {currentLength}/{maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea'; 